import React, { useState, useRef, useEffect, useCallback } from 'react';

/**
 * InlineEdit - Click-to-edit field supporting text, number, date, and select types.
 *
 * @param {{ value: any, onSave: (v: any) => Promise<void>, type?: string, options?: Array<{value:string,label:string}>, placeholder?: string }} props
 */
export default function InlineEdit({
  value,
  onSave,
  type = 'text',
  options,
  placeholder = 'Click to edit',
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (type === 'text' && inputRef.current.select) {
        inputRef.current.select();
      }
    }
  }, [editing, type]);

  const commit = useCallback(async () => {
    if (draft === value) {
      setEditing(false);
      return;
    }
    setError(false);
    setSaving(true);
    try {
      await onSave(draft);
      setEditing(false);
    } catch {
      setError(true);
    } finally {
      setSaving(false);
    }
  }, [draft, value, onSave]);

  const cancel = useCallback(() => {
    setDraft(value);
    setEditing(false);
    setError(false);
  }, [value]);

  const handleKeyDown = useCallback(
    (e) => {
      if (type === 'textarea') {
        // Textarea: only Escape cancels, blur commits (Enter inserts newline)
        if (e.key === 'Escape') cancel();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        commit();
      } else if (e.key === 'Escape') {
        cancel();
      }
    },
    [commit, cancel, type]
  );

  // Select type: always interactive, auto-saves on change (no click-to-edit)
  if (type === 'select' && options) {
    const selectClasses = [
      'min-h-[44px] bg-surface-0 text-text-primary text-[13px] px-2 py-1 w-full',
      'border outline-none',
      error ? 'border-error' : 'border-glass-border focus:border-accent',
      saving ? 'opacity-50 pointer-events-none' : '',
    ].join(' ');

    return (
      <div className="relative inline-block w-full">
        <select
          value={draft ?? ''}
          onChange={async (e) => {
            const newVal = e.target.value;
            setDraft(newVal);
            if (String(newVal) === String(value)) return;
            setError(false);
            setSaving(true);
            try {
              await onSave(newVal);
            } catch {
              setError(true);
            } finally {
              setSaving(false);
            }
          }}
          disabled={saving}
          className={selectClasses}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        {saving && <Spinner />}
      </div>
    );
  }

  // Display mode
  if (!editing) {
    let displayText = value;
    if (type === 'select' && options) {
      const match = options.find((o) => String(o.value) === String(value));
      if (match) displayText = match.label;
    }
    return (
      <button
        type="button"
        className="inline-flex min-h-[44px] max-w-full cursor-pointer items-center bg-transparent p-0 text-left text-inherit hover:underline hover:decoration-dotted hover:underline-offset-4 hover:decoration-text-tertiary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        onClick={() => setEditing(true)}
        aria-label={`Edit ${displayText != null && displayText !== '' ? String(displayText) : placeholder}`}
      >
        {displayText != null && displayText !== '' ? String(displayText) : (
          <span className="text-text-tertiary italic">{placeholder}</span>
        )}
      </button>
    );
  }

  const borderClass = error ? 'border-error' : 'border-glass-border focus:border-accent';

  const sharedClasses = [
    'min-h-[44px] bg-surface-0 text-text-primary text-[13px] px-2 py-1 w-full',
    'border outline-none',
    borderClass,
    saving ? 'opacity-50 pointer-events-none' : '',
  ].join(' ');

  // Textarea type
  if (type === 'textarea') {
    return (
      <div className="relative inline-block w-full">
        <textarea
          ref={inputRef}
          rows={3}
          value={draft ?? ''}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`${sharedClasses} resize-y`}
        />
        {saving && <Spinner />}
      </div>
    );
  }

  // Text / number / date
  return (
    <div className="relative inline-block w-full">
      <input
        ref={inputRef}
        type={type}
        value={draft ?? ''}
        onChange={(e) =>
          setDraft(type === 'number' ? Number(e.target.value) : e.target.value)
        }
        onBlur={commit}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={sharedClasses}
      />
      {saving && <Spinner />}
    </div>
  );
}

function Spinner() {
  return (
    <span className="absolute right-2 top-1/2 -translate-y-1/2">
      <svg
        className="animate-spin h-3 w-3 text-accent"
        viewBox="0 0 24 24"
        fill="none"
      >
        <circle
          cx="12" cy="12" r="10"
          stroke="currentColor" strokeWidth="3" opacity="0.25"
        />
        <path
          d="M4 12a8 8 0 018-8"
          stroke="currentColor" strokeWidth="3" strokeLinecap="round"
        />
      </svg>
    </span>
  );
}
